import { ActionConfirmationHost } from "../ui/ActionConfirmation.js";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { get, send, type BookingList } from "../api.js";
import { hasActionCentreAccess, useActionCentre } from "../actionCentre.js";
import { canManageSchoolDomain, isOwner, useAuth } from "../auth.js";
import {
  diaryPaymentClassifications,
  diaryPaymentIndicator,
  type DiaryPaymentClassification,
} from "../booking/diaryPayments.js";
import { scheduling } from "../booking/scheduling.js";
import { staffBooking } from "../booking/staff.js";
import { diaryEntries, type DiaryEntry } from "../calendar/entries.js";
import { LessonActionDrawer } from "../calendar/LessonActionDrawer.js";
import { dayOf, sessionsByColumn, type Session } from "../calendar/grid.js";
import { schoolLessonWriteupHref } from "../calendar/schoolLessonIdentity.js";
import { dismissFollowUp, getFollowUpDismissals, restoreFollowUp } from "../followUp.js";
import { formatDate, formatDay, formatTime, isoDay } from "../format.js";
import { NoteEnvelope } from "../NoteEnvelope.js";
import { listUnassignedSchoolPupils, schoolPupilHref } from "../schoolPupils.js";
import { useNewSinceLoad } from "../sync.js";
import { useAsync } from "../useAsync.js";
import { useNow } from "../useNow.js";
import {
  describeCountdown,
  schoolDaySummary,
  schoolIdentityOf,
  upNextState,
  type ReadableEntry,
} from "./dashboardDay.js";
import { studentDetailHref } from "./Students.js";
import { TodayPaymentsDue } from "./PaymentsDue.js";

export {
  describeCountdown,
  schoolDaySummary,
  schoolIdentityOf,
  upNextState,
} from "./dashboardDay.js";

const NOTES_LOOKBACK_DAYS = 14;
const FOLLOW_UP_ALL_LOOKBACK_DAYS = 90;
const FOLLOW_UP_LIMIT = 6;

export function ownDashboardEntries(
  entries: readonly DiaryEntry[],
  tutorProfileId: string | null,
  owner: boolean,
): ReadableEntry[] {
  const readable = entries.filter((entry): entry is ReadableEntry => !entry.anonymous);
  if (tutorProfileId !== null) return readable.filter((entry) => entry.tutorId === tutorProfileId);
  // A tutor's named rows are already server-scoped to their own lessons. An
  // owner sees everybody named, so without a tutor identity we show nobody
  // rather than silently turning a personal dashboard into a school dashboard.
  return owner ? [] : readable;
}

/**
 * Past lessons still owed a published note. School lessons count like any
 * other: the Hub now projects their note state onto the diary row, so a
 * School lesson taught this morning with nothing written is on the list.
 */
export function notesToWrite(entries: readonly ReadableEntry[], now = Date.now()): ReadableEntry[] {
  return entries.filter((entry) => {
    const starts = Date.parse(entry.startsAt);
    return Number.isFinite(starts) && starts < now && entry.lessonPublishedAt === null;
  });
}

/**
 * The follow-up list as Home shows it: lessons still owed a note, minus the
 * ones this tutor has taken off the list, newest first so the lesson taught
 * an hour ago sits at the top rather than one from a fortnight back.
 */
export function followUpList(
  entries: readonly ReadableEntry[],
  dismissedKeys: ReadonlySet<string>,
  now = Date.now(),
): ReadableEntry[] {
  return notesToWrite(entries, now)
    .filter((entry) => !dismissedKeys.has(entry.bookingId))
    .sort((a, b) => Date.parse(b.startsAt) - Date.parse(a.startsAt));
}

/** Where a row's note is written: the School write-up for a School lesson, the lesson page otherwise. */
export function noteHrefFor(entry: ReadableEntry): string {
  return entry.isSchools
    ? (schoolLessonWriteupHref(entry.bookingId) ?? "/schools")
    : `/bookings/${entry.bookingId}/lesson`;
}

/**
 * `useAsync` keeps the last-loaded value on a failed reload rather than
 * clearing it, so `noteTasks` can be non-empty while `recent.error` is set.
 * The follow-up panel itself already gates on `recent.error` before
 * rendering `noteTasks` directly; anything that links to or badges off the
 * count from *outside* that panel must not act on a total it cannot
 * currently stand behind.
 */
export function reliableNoteTasks<T>(
  noteTasks: readonly T[],
  recentError: string | null,
): readonly T[] {
  return recentError === null ? noteTasks : [];
}

export type DashboardLessonNoteState = "No note" | "Draft" | "Published";

/** The same three lesson-note states already represented by the Diary data. */
export function dashboardLessonNoteState(entry: {
  lessonId: string | null;
  lessonPublishedAt: string | null;
}): DashboardLessonNoteState {
  if (entry.lessonPublishedAt !== null) return "Published";
  return entry.lessonId === null ? "No note" : "Draft";
}

/** Human-readable booking state; never rely on badge colour alone. */
export function dashboardBookingStatusLabel(status: string): string {
  const normalised = status.trim().toLowerCase();
  const known: Record<string, string> = {
    approved: "Confirmed",
    pending: "Pending",
    canceled: "Cancelled",
    rejected: "Rejected",
    "no-show": "No-show",
  };
  if (known[normalised]) return known[normalised]!;
  if (normalised === "") return "Unknown status";
  return normalised
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => `${word[0]?.toLocaleUpperCase("en-GB") ?? ""}${word.slice(1)}`)
    .join(" ");
}

/** Compact London-time summary for the Today header. */
export function todayScheduleSummary(entries: readonly ReadableEntry[]): string {
  if (entries.length === 0) return "No lessons booked for you today.";
  const ordered = [...entries].sort(
    (left, right) => Date.parse(left.startsAt) - Date.parse(right.startsAt),
  );
  const first = ordered[0]!;
  const last = ordered.at(-1)!;
  const noun = ordered.length === 1 ? "lesson" : "lessons";
  return `${ordered.length} ${noun} · first at ${formatTime(first.startsAt)} · last at ${formatTime(last.endsAt)}`;
}

/**
 * The "Nothing needs attention right now." line is an affirmative business
 * claim, so it may only appear once every source it summarises has actually
 * loaded. A queue that is still loading, or that failed and fell back to an
 * empty/zero value, must not be read as "clear".
 */
export function nothingNeedsAttention(input: {
  loading: boolean;
  failed: boolean;
  operationsCount: number;
  unmatchedCount: number;
  overdueCount: number;
  unassignedPupilCount?: number;
}): boolean {
  if (input.loading || input.failed) return false;
  return (
    input.operationsCount === 0 &&
    input.unmatchedCount === 0 &&
    input.overdueCount === 0 &&
    (input.unassignedPupilCount ?? 0) === 0
  );
}

/**
 * Today only needs the overdue count. The actionable unpaid-lessons report
 * deliberately revalidates every candidate against live Amelia data and can
 * fail closed on one stale row; keep that stricter path for the detailed
 * report page rather than making the Today summary depend on it.
 */
export function loadTodayPaymentReport(todayKey: string) {
  return staffBooking.payments({ start_date: isoDay(-30), end_date: todayKey, scope: "mine" });
}

/**
 * Dashboard rows are appointment entry points, not one row per participant.
 * Group them with the same calendar primitive as the Diary so opening a shared
 * appointment preserves participant count and therefore the drawer's live
 * payment/attendance eligibility.
 */
export function dashboardAppointmentSessions(
  entries: readonly DiaryEntry[],
): Session<DiaryEntry>[] {
  return sessionsByColumn(entries, ["dashboard"], () => "dashboard").get("dashboard") ?? [];
}

/**
 * Mirror the Diary: an Amelia-backed appointment can use the generic drawer,
 * and a canonical School session can open the same shell because the drawer's
 * School branch exposes only School-safe actions. A private row with no valid
 * Amelia id remains non-openable.
 */
export function dashboardAppointmentIsOpenable(session: Session<DiaryEntry>): boolean {
  const first = session.bookings.find((entry): entry is ReadableEntry => !entry.anonymous);
  if (first === undefined) return false;
  if (first.isSchools) return true;
  return (
    typeof first.externalId === "number" &&
    Number.isInteger(first.externalId) &&
    first.externalId > 0
  );
}

function nestedInteractiveTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest("a, button, input, select, textarea, label") !== null
  );
}

function studentName(entry: ReadableEntry): string {
  return entry.studentName ?? entry.customerName ?? "Unknown student";
}

/**
 * The student on a row, linked to their record where there is one to link to.
 *
 * Two things this deliberately does not do. It never invents a record from a
 * customer name: an unmatched booking says so, in the same words the diary
 * uses, rather than being quietly unclickable for a reason nobody can see. And
 * it opens the record in the roster this page is already showing — every row
 * here is the signed-in tutor's own teaching, so an owner who also teaches
 * lands back in My students instead of the whole school.
 *
 * Canonical School pupils are a third, explicit case: they have no private
 * Student FK by design, so they are labelled School rather than "not matched".
 */
export function DashboardStudentName({ entry }: { entry: ReadableEntry }) {
  const name = studentName(entry);
  if (entry.isSchools) {
    const identity = schoolIdentityOf(entry);
    return (
      <span className="dashboard-student-unmatched">
        <strong>
          {identity ? (
            <Link
              className="workflow-action-link dashboard-student-link"
              to={schoolPupilHref(identity.pupilId)}
            >
              {name}
            </Link>
          ) : (
            name
          )}
        </strong>
        <span className="tag">School</span>
      </span>
    );
  }
  if (entry.studentId === null) {
    // One element, not a fragment: the lesson row stacks its children, so a
    // loose tag beside the name would drop onto a line of its own.
    return (
      <span className="dashboard-student-unmatched">
        <strong>{name}</strong>
        <span className="tag warn">not matched</span>
      </span>
    );
  }
  return (
    <strong>
      <Link
        className="workflow-action-link dashboard-student-link"
        to={studentDetailHref(entry.studentId, "mine")}
      >
        {name}
      </Link>
    </strong>
  );
}

/**
 * Staff names occasionally arrive as lower-case usernames. Prefer the tutor
 * profile's real display name, then make word/apostrophe boundaries readable
 * without lower-casing deliberate capitals that may already be present.
 */
export function dashboardGreetingName(
  displayName: string | null | undefined,
  email: string | null | undefined,
  username: string | null | undefined,
): string {
  const source = displayName ?? username ?? email?.split("@")[0] ?? "there";
  const trimmed = source.trim();
  if (trimmed === "") return "there";
  return trimmed.replace(
    /(^|[\s'’._-])(\p{L})/gu,
    (_match, prefix: string, letter: string) => `${prefix}${letter.toLocaleUpperCase("en-GB")}`,
  );
}

/** The time now, in the Hub's own timezone, ticking once a second. */
export function TodayClock() {
  const now = useNow(1_000);
  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(now));
  return (
    <time
      className="dashboard-clock tabular"
      dateTime={new Date(now).toISOString()}
      aria-live="off"
    >
      {time}
    </time>
  );
}

/**
 * What is happening now and what is next, or the good news that the day is
 * over. Reads the same rows as the list below; the only extra ingredient is
 * the clock.
 */
export function UpNextPanel({
  entries,
  loading,
  error,
  now,
  notesOutstanding,
}: {
  entries: readonly ReadableEntry[];
  loading: boolean;
  error: string | null;
  now: number;
  notesOutstanding: number;
}) {
  const state = upNextState(entries, now);
  const lesson =
    state.kind === "in-progress" ? state.current : state.kind === "up-next" ? state.next : null;

  return (
    <section
      className="dashboard-panel dashboard-up-next"
      aria-labelledby="dashboard-up-next-heading"
    >
      <div className="dashboard-panel-head">
        <div>
          <span className="small muted">
            {state.kind === "in-progress"
              ? "Teaching now"
              : state.kind === "up-next"
                ? "Up next"
                : "Today"}
          </span>
          <h2 id="dashboard-up-next-heading">
            {loading
              ? "Loading your day…"
              : error
                ? "Today’s lessons could not be loaded"
                : state.kind === "none"
                  ? "No lessons today"
                  : state.kind === "done"
                    ? "You’re done for the day"
                    : `${studentName(lesson!)} · ${formatTime(lesson!.startsAt)}`}
          </h2>
        </div>
        {!loading && !error && state.kind !== "none" && (
          <span className="dashboard-up-next-count small muted">
            {state.kind === "done"
              ? `${state.taught} ${state.taught === 1 ? "lesson" : "lessons"} taught`
              : `${state.remaining} still to come`}
          </span>
        )}
      </div>
      {!loading && !error && lesson && (
        <div className="dashboard-up-next-body">
          <div className="dashboard-up-next-when">
            {state.kind === "in-progress"
              ? `Ends ${describeCountdown(state.endsInMs)} at ${formatTime(lesson.endsAt)}`
              : `Starts ${describeCountdown((state as { startsInMs: number }).startsInMs)}`}
          </div>
          <div className="small muted">
            {lesson.serviceName ?? lesson.instrument ?? "Lesson"}
            {lesson.isSchools ? "" : lesson.tutorName ? ` · ${lesson.tutorName}` : ""}
          </div>
          <div className="dashboard-up-next-actions">
            <DashboardStudentName entry={lesson} />
            {state.kind === "in-progress" && state.next && (
              <span className="small muted">
                Then {studentName(state.next)} at {formatTime(state.next.startsAt)}
              </span>
            )}
          </div>
        </div>
      )}
      {!loading && !error && state.kind === "done" && (
        <div className="dashboard-up-next-body">
          <div className="dashboard-up-next-when">
            Last lesson finished at {formatTime(state.lastEndedAt)}.
          </div>
          <div className="small muted">
            {notesOutstanding === 0 ? (
              "Every lesson note is written."
            ) : (
              <a href="#notes-to-write">
                {notesOutstanding} {notesOutstanding === 1 ? "note" : "notes"} still to write
              </a>
            )}
          </div>
        </div>
      )}
      {!loading && !error && state.kind === "none" && (
        <div className="dashboard-up-next-body">
          <div className="small muted">Nothing is booked for you today.</div>
        </div>
      )}
    </section>
  );
}

const QUICK_ATTENDANCE: Array<{ value: string; label: string }> = [
  { value: "present", label: "Attended" },
  { value: "late", label: "Late" },
  { value: "absent", label: "Absent" },
];

/**
 * One tap of attendance for a School pupil, from the row itself.
 *
 * Attendance already exists in the Hub but was only reachable from inside the
 * write-up. Marking it here is the same write the write-up makes.
 */
export function SchoolAttendanceQuickMark({
  entry,
  onChanged,
}: {
  entry: ReadableEntry;
  onChanged: () => void;
}) {
  const identity = schoolIdentityOf(entry);
  const [saving, setSaving] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  if (!identity) return null;

  async function mark(value: string) {
    if (!identity) return;
    setSaving(value);
    setProblem(null);
    try {
      await send(
        "PUT",
        "/school-domain/attendance",
        { sessionId: identity.sessionId, pupilId: identity.pupilId, attendanceStatus: value },
        { "Idempotency-Key": crypto.randomUUID() },
      );
      onChanged();
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : "Could not record attendance.");
    } finally {
      setSaving(null);
    }
  }

  return (
    <span className="dashboard-attendance" aria-label={`Attendance for ${studentName(entry)}`}>
      {QUICK_ATTENDANCE.map((option) => {
        const current = entry.attendanceStatus === option.value;
        return (
          <button
            key={option.value}
            type="button"
            className={`dashboard-attendance-option${current ? " is-current" : ""}`}
            aria-pressed={current}
            disabled={saving !== null}
            onClick={() => void mark(option.value)}
          >
            {saving === option.value ? "…" : option.label}
          </button>
        );
      })}
      {problem && <span className="error small">{problem}</span>}
    </span>
  );
}

/** The day at each school: who, when, and what is still owed. */
export function SchoolDayPanel({
  entries,
  now,
}: {
  entries: readonly ReadableEntry[];
  now: number;
}) {
  const schools = schoolDaySummary(entries, now);
  if (schools.length === 0) return null;
  return (
    <section
      className="dashboard-panel dashboard-school-day"
      aria-labelledby="dashboard-school-day-heading"
    >
      <div className="dashboard-panel-head">
        <h2 id="dashboard-school-day-heading">School day</h2>
        <Link to="/schools">Schools</Link>
      </div>
      {schools.map((school) => (
        <div className="dashboard-note-row" key={school.school}>
          <div>
            <strong>{school.school}</strong>
            <div className="small muted">
              {school.pupils} {school.pupils === 1 ? "pupil" : "pupils"} ·{" "}
              {formatTime(school.firstStartsAt)}–{formatTime(school.lastEndsAt)}
            </div>
          </div>
          <span className="small muted dashboard-school-day-owed">
            {school.notesOutstanding === 0 && school.attendanceMissing === 0
              ? "Up to date"
              : [
                  school.notesOutstanding > 0
                    ? `${school.notesOutstanding} ${school.notesOutstanding === 1 ? "note" : "notes"} to write`
                    : null,
                  school.attendanceMissing > 0
                    ? `${school.attendanceMissing} attendance ${school.attendanceMissing === 1 ? "mark" : "marks"} missing`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
          </span>
        </div>
      ))}
    </section>
  );
}

function MetricCard({
  label,
  value,
  detail,
  to,
  tone = "default",
}: {
  label: string;
  value: string | number;
  detail: string;
  to?: string;
  tone?: "default" | "warn" | "danger";
}) {
  return (
    <article className={`dashboard-metric dashboard-tone-${tone}`}>
      <div className="dashboard-metric-label">{label}</div>
      <strong className="dashboard-metric-value">{value}</strong>
      <div className="small muted">{detail}</div>
      {to && (
        <Link className="dashboard-card-link" to={to}>
          View
        </Link>
      )}
    </article>
  );
}

export function Dashboard() {
  return <ActionConfirmationHost><DashboardContent /></ActionConfirmationHost>;
}

function DashboardContent() {
  const { me } = useAuth();
  if (!me) return null;

  const actionCentre = useActionCentre();
  const [selectedLesson, setSelectedLesson] = useState<Session<DiaryEntry> | null>(null);
  const [paymentRevision, setPaymentRevision] = useState(0);
  const todayHeading = useRef<HTMLHeadingElement>(null);
  const focusTodayHeadingAfterMutation = useRef(false);
  const held = new Set(me.capabilities);
  const owner = isOwner(me);
  const canSeePayments = me.booking.staff && held.has("reports.payments.read");
  const canSeePaymentDetails =
    me.booking.staff && (held.has("payments.manage") || held.has("reports.payments.read"));
  const canSeeActionCentre = hasActionCentreAccess(actionCentre.access);
  const canSeeRequests = actionCentre.access.requests;
  const canSeeOperationsReport = actionCentre.access.reports;
  const canSeeWaitlist = actionCentre.access.waitlist;

  const todayKey = isoDay(0);
  const identity = useAsync(() => scheduling.me(), []);
  // One personal mirror read covers both Today and the 14-calendar-day note
  // lookback. `scope=mine` is derived from the authenticated principal by the
  // server, so an owner who also teaches no longer downloads the whole school
  // merely to filter it in this component. The server now adds only that same
  // caller's canonical School rows to this personal read.
  const recent = useAsync(
    () =>
      get<BookingList>("/admin/bookings", {
        from: isoDay(-(NOTES_LOOKBACK_DAYS - 1)),
        to: todayKey,
        scope: "mine",
      }),
    [todayKey],
    { reloadOnMirrorRefresh: true },
  );
  // Keep the established Today loading/error API below while sharing the same
  // hook instance rather than issuing a second overlapping request.
  const today = recent;

  // Home stays cheap by default. The wider history is fetched only when the
  // tutor asks to go beyond the recent preview, then it is still revealed in
  // small groups rather than dumped into the page at once.
  const [showAllFollowUp, setShowAllFollowUp] = useState(false);
  const wider = useAsync(
    () =>
      showAllFollowUp
        ? get<BookingList>("/admin/bookings", {
            from: isoDay(-(FOLLOW_UP_ALL_LOOKBACK_DAYS - 1)),
            to: todayKey,
            scope: "mine",
          })
        : Promise.resolve(null),
    [showAllFollowUp, todayKey],
    { reloadOnMirrorRefresh: true },
  );
  const [followUpVisibleCount, setFollowUpVisibleCount] = useState(FOLLOW_UP_LIMIT);
  // Lessons this tutor has chosen not to be reminded about. If the read fails
  // the list simply shows everything again; nothing is hidden by mistake.
  const dismissals = useAsync(() => getFollowUpDismissals(), []);
  const [locallyDismissed, setLocallyDismissed] = useState<ReadonlySet<string>>(new Set());
  const [selectedFollowUp, setSelectedFollowUp] = useState<ReadonlySet<string>>(new Set());
  const [followUpBusy, setFollowUpBusy] = useState(false);
  const [followUpProblem, setFollowUpProblem] = useState<string | null>(null);
  const [lastDismissed, setLastDismissed] = useState<string[] | null>(null);

  useEffect(() => {
    if (selectedLesson !== null || !focusTodayHeadingAfterMutation.current) return;
    focusTodayHeadingAfterMutation.current = false;
    todayHeading.current?.focus({ preventScroll: true });
  }, [selectedLesson]);

  const tutorProfileId = identity.data?.tutorProfileId ?? null;
  const recentMine = ownDashboardEntries(diaryEntries(recent.data), tutorProfileId, owner);
  const todayMine = recentMine.filter((entry) => dayOf(entry.startsAt) === todayKey);
  const todaySessions = dashboardAppointmentSessions(todayMine);
  const todayAppointments = todaySessions
    .map((session) => session.bookings.find((entry): entry is ReadableEntry => !entry.anonymous))
    .filter((entry): entry is ReadableEntry => entry !== undefined);
  const dismissedKeys = new Set([...(dismissals.data?.bookingKeys ?? []), ...locallyDismissed]);
  const widerMine =
    showAllFollowUp && wider.data !== null
      ? ownDashboardEntries(diaryEntries(wider.data), tutorProfileId, owner)
      : null;
  const followUpSource = widerMine ?? recentMine;
  const followUpLoading = recent.loading || (showAllFollowUp && wider.loading);
  const followUpError = recent.error ?? (showAllFollowUp ? wider.error : null);
  const noteTasks = followUpList(followUpSource, dismissedKeys);
  const noteTasksReliable = reliableNoteTasks(noteTasks, followUpError);
  const visibleNoteTasks = noteTasks.slice(0, followUpVisibleCount);
  const selectedVisible = visibleNoteTasks.filter((entry) => selectedFollowUp.has(entry.bookingId));

  useEffect(() => {
    const appointmentId = selectedLesson?.appointmentId;
    if (appointmentId === undefined) return;
    const latestRecent = ownDashboardEntries(diaryEntries(recent.data), tutorProfileId, owner);
    const latestWider = showAllFollowUp && wider.data !== null
      ? ownDashboardEntries(diaryEntries(wider.data), tutorProfileId, owner)
      : [];
    const refreshed = dashboardAppointmentSessions(latestRecent).find(
      (session) => session.appointmentId === appointmentId,
    ) ?? dashboardAppointmentSessions(latestWider).find(
      (session) => session.appointmentId === appointmentId,
    );
    if (!refreshed) return;
    setSelectedLesson((current) =>
      current?.appointmentId === appointmentId && current !== refreshed ? refreshed : current,
    );
  }, [owner, recent.data, selectedLesson?.appointmentId, showAllFollowUp, tutorProfileId, wider.data]);

  function toggleFollowUpSelection(bookingKey: string, selected: boolean) {
    setSelectedFollowUp((current) => {
      const next = new Set(current);
      if (selected) next.add(bookingKey);
      else next.delete(bookingKey);
      return next;
    });
  }

  async function removeSelectedFromFollowUp() {
    const keys = selectedVisible.map((entry) => entry.bookingId);
    if (keys.length === 0 || followUpBusy) return;
    setFollowUpBusy(true);
    setFollowUpProblem(null);
    try {
      await dismissFollowUp(keys);
      setLocallyDismissed((current) => new Set([...current, ...keys]));
      setSelectedFollowUp(new Set());
      setLastDismissed(keys);
      dismissals.reload();
    } catch (cause) {
      setFollowUpProblem(
        cause instanceof Error ? cause.message : "Could not remove those lessons from follow-up.",
      );
    } finally {
      setFollowUpBusy(false);
    }
  }

  async function undoLastDismissal() {
    if (lastDismissed === null || followUpBusy) return;
    const keys = lastDismissed;
    setFollowUpBusy(true);
    setFollowUpProblem(null);
    try {
      await restoreFollowUp(keys);
      setLocallyDismissed((current) => {
        const next = new Set(current);
        for (const key of keys) next.delete(key);
        return next;
      });
      setLastDismissed(null);
      dismissals.reload();
    } catch (cause) {
      setFollowUpProblem(
        cause instanceof Error ? cause.message : "Could not put those lessons back on follow-up.",
      );
    } finally {
      setFollowUpBusy(false);
    }
  }
  const bookingsChanged = useNewSinceLoad(today.loading);

  const paymentIds = canSeePaymentDetails
    ? [
        ...new Set(
          todayAppointments
            .map((entry) => entry.externalId)
            .filter((id): id is number => id !== null),
        ),
      ]
    : [];
  const paymentKey = paymentIds.sort((a, b) => a - b).join(",");
  const paymentStates = useAsync(
    () =>
      paymentKey === ""
        ? Promise.resolve([] as DiaryPaymentClassification[])
        : diaryPaymentClassifications(paymentKey.split(",").map(Number)),
    [paymentKey],
  );
  const paymentByAppointment = new Map(
    (paymentStates.data ?? []).map((payment) => [payment.appointment_id, payment] as const),
  );

  const paymentReport = useAsync(
    () => (canSeePayments ? loadTodayPaymentReport(todayKey) : Promise.resolve(null)),
    [canSeePayments, todayKey],
  );

  const requestCount = actionCentre.countFor("requests") ?? 0;
  const clashCount = actionCentre.countFor("clashes") ?? 0;
  const exceptionCount = actionCentre.countFor("exceptions") ?? 0;
  const waitlistCount = actionCentre.countFor("waitlist") ?? 0;
  const unmatchedCount = actionCentre.countFor("unmatched") ?? 0;
  const operationsCount = requestCount + clashCount + exceptionCount + waitlistCount;
  const actionCentreCount = actionCentre.total ?? operationsCount + unmatchedCount;

  const overdueCount = canSeePayments ? (paymentReport.data?.past_unpaid_lessons ?? 0) : 0;
  const attentionLoading = actionCentre.loading || (canSeePayments && paymentReport.loading);
  const attentionFailed = Boolean(
    actionCentre.failed ||
    (canSeeActionCentre && actionCentre.total === null) ||
    (canSeePayments && paymentReport.error),
  );

  const greeting = dashboardGreetingName(
    identity.data?.displayName,
    me.user.email,
    me.user.username,
  );
  // Half a minute is enough for "starts in 12 min" to stay honest; the clock
  // in the header ticks on its own.
  const now = useNow(30_000);
  const todayInstant = new Date(now).toISOString();
  const schoolDomain = canManageSchoolDomain(me);
  const unassignedPupils = useAsync(
    () => (schoolDomain ? listUnassignedSchoolPupils() : Promise.resolve(null)),
    [schoolDomain],
  );
  const unassignedPupilCount = unassignedPupils.data?.pupils.length ?? 0;
  const todayNotesOutstanding = followUpList(todayMine, dismissedKeys, now).length;

  function refreshAppointmentSourcesInPlace() {
    recent.reload();
    if (showAllFollowUp) wider.reload();
    paymentStates.reload();
    if (canSeePayments) paymentReport.reload();
    setPaymentRevision((current) => current + 1);
  }

  function refreshAppointmentSources() {
    focusTodayHeadingAfterMutation.current = true;
    setSelectedLesson(null);
    refreshAppointmentSourcesInPlace();
  }

  return (
    <div className="dashboard-page">
      <header className="dashboard-head">
        <div>
          <h1>
            Today <span aria-hidden="true">—</span> {formatDay(todayInstant)}
          </h1>
          <p className="lede">
            {today.loading
              ? "Loading your teaching day…"
              : today.error
                ? "Today’s bookings could not be loaded. Other Hub sections below may still be available."
                : todayScheduleSummary(todayAppointments)}
          </p>
        </div>
        <div className="dashboard-date">
          <TodayClock />
          <span>Teaching view for {greeting}</span>
        </div>
      </header>

      <UpNextPanel
        entries={todayAppointments}
        loading={today.loading}
        error={today.error}
        now={now}
        notesOutstanding={todayNotesOutstanding}
      />

      {bookingsChanged && (
        <div className="notice dashboard-refresh">
          <span>Bookings have changed since Today loaded.</span>
          <button className="quiet small" type="button" onClick={today.reload}>
            Refresh today
          </button>
        </div>
      )}

      {identity.error && owner && (
        <div className="notice small">
          Your teaching identity could not be loaded, so Today is not showing organisation-wide
          lessons in its place.
        </div>
      )}

      <section className="dashboard-metrics" aria-label="Today at a glance">
        <MetricCard
          label="Lessons today"
          value={today.loading ? "…" : today.error ? "—" : todayAppointments.length}
          detail="Your scheduled lessons"
          to="/diary"
        />
        <MetricCard
          label="Teaching follow-up"
          value={followUpLoading ? "…" : followUpError ? "—" : noteTasks.length}
          detail={`No note or draft from the last ${showAllFollowUp ? FOLLOW_UP_ALL_LOOKBACK_DAYS : NOTES_LOOKBACK_DAYS} days`}
          to={noteTasksReliable.length > 0 ? "#notes-to-write" : undefined}
          tone={noteTasksReliable.length > 0 ? "warn" : "default"}
        />
        {canSeePayments && (
          <MetricCard
            label="Overdue payments"
            value={paymentReport.loading ? "…" : paymentReport.error ? "—" : overdueCount}
            detail={
              paymentReport.error
                ? "Could not load your payment report"
                : "Past lessons still unpaid"
            }
            to={paymentReport.error ? undefined : "/reports/unpaid-lessons"}
            tone={overdueCount > 0 ? "danger" : "default"}
          />
        )}
        {canSeeActionCentre && (
          <MetricCard
            label="Action Centre"
            value={
              actionCentre.loading ? "…" : actionCentre.total === null ? "—" : actionCentreCount
            }
            detail={
              actionCentre.failed || actionCentre.total === null
                ? "Could not determine every Action Centre count"
                : "Requests, clashes, exceptions, waiting list and unmatched work"
            }
            to={actionCentre.failed || actionCentre.total === null ? undefined : "/waiting"}
            tone={
              !actionCentre.failed && actionCentre.total !== null && actionCentreCount > 0
                ? "warn"
                : "default"
            }
          />
        )}
      </section>

      <TodayPaymentsDue revision={paymentRevision} />

      <div className="dashboard-grid">
        <section
          className="dashboard-panel dashboard-today"
          aria-labelledby="dashboard-today-heading"
        >
          <div className="dashboard-panel-head">
            <div>
              <h2 ref={todayHeading} tabIndex={-1} id="dashboard-today-heading">
                Today&rsquo;s lessons
              </h2>
              <span className="small muted">
                {today.loading
                  ? "Loading…"
                  : today.error
                    ? "Schedule unavailable"
                    : `${todayAppointments.length} scheduled`}
              </span>
            </div>
            <Link to="/diary">View full diary</Link>
          </div>
          {today.loading && <p className="empty">Loading today&rsquo;s lessons…</p>}
          {today.error && <div className="error">Today&rsquo;s bookings could not be loaded.</div>}
          {!today.loading && !today.error && todayAppointments.length === 0 && (
            <p className="empty">No lessons booked for you today.</p>
          )}
          {todaySessions.map((session) => {
            const entry = session.bookings.find(
              (candidate): candidate is ReadableEntry => !candidate.anonymous,
            );
            if (!entry) return null;
            const payment =
              entry.externalId === null ? undefined : paymentByAppointment.get(entry.externalId);
            const paymentIndicator =
              canSeePaymentDetails && !entry.isSchools
                ? diaryPaymentIndicator(payment, entry.endsAt, new Date(now))
                : null;
            const lessonHasStarted = Date.parse(entry.startsAt) < now;
            const noteState = dashboardLessonNoteState(entry);
            const noteNeedsAttention = lessonHasStarted && noteState !== "Published";
            const openable = dashboardAppointmentIsOpenable(session);
            const noteHref = noteHrefFor(entry);
            return (
              <div
                className="dashboard-lesson-row"
                key={session.appointmentId}
                // Clicking anywhere on the row opens the appointment, but that
                // is a mouse convenience only — the row is not itself a button.
                // A `role="button"` here would make everything inside it
                // presentational to assistive technology, which took the
                // student link and lesson-note action out of reach for the
                // people relying on it while leaving them there for everybody
                // else. The keyboard and screen readers get the real control
                // at the end of the row instead.
                onClick={
                  openable
                    ? (event) => {
                        if (nestedInteractiveTarget(event.target)) return;
                        // Focus that control first: the drawer returns focus to
                        // whatever was active when it opened, and a click on the row
                        // itself would otherwise leave that nowhere.
                        event.currentTarget
                          .querySelector<HTMLElement>(".dashboard-row-open")
                          ?.focus({ preventScroll: true });
                        setSelectedLesson(session);
                      }
                    : undefined
                }
                style={openable ? { cursor: "pointer" } : undefined}
              >
                <time dateTime={entry.startsAt}>{formatTime(entry.startsAt)}</time>
                <div className="dashboard-lesson-main">
                  <DashboardStudentName entry={entry} />
                  <span className="small muted">
                    {entry.serviceName ?? entry.instrument ?? "Lesson"}
                    {entry.tutorName ? ` · ${entry.tutorName}` : ""}
                  </span>
                </div>
                <span className="tag">
                  {entry.isSchools ? "School" : dashboardBookingStatusLabel(entry.bookingStatus)}
                </span>
                <span className={noteNeedsAttention ? "tag warn" : "tag"}>
                  {noteState === "Published" && (
                    <NoteEnvelope
                      sent={entry.lessonNoteSent === true}
                      className="dashboard-envelope"
                    />
                  )}
                  {noteState}
                </span>
                {entry.isSchools && schoolDomain && lessonHasStarted && (
                  <SchoolAttendanceQuickMark entry={entry} onChanged={() => recent.reload()} />
                )}
                {noteNeedsAttention ? (
                  <Link className="workflow-action-link dashboard-row-action" to={noteHref}>
                    {noteState === "Draft" ? "Edit draft" : "Write note"}
                  </Link>
                ) : noteState === "Published" ? (
                  <Link className="workflow-action-link dashboard-row-action" to={noteHref}>
                    View note
                  </Link>
                ) : entry.isSchools ? (
                  <Link className="workflow-action-link dashboard-row-action" to={noteHref}>
                    Open lesson
                  </Link>
                ) : null}
                {paymentIndicator && (
                  <span className={`dashboard-payment dashboard-payment-${paymentIndicator.tone}`}>
                    {paymentIndicator.label}
                  </span>
                )}
                {openable && (
                  <button
                    type="button"
                    className="dashboard-row-open"
                    aria-haspopup="dialog"
                    aria-label={
                      entry.isSchools
                        ? `Open School lesson for ${studentName(entry)}`
                        : `Open ${entry.serviceName ?? entry.instrument ?? "lesson"} appointment for ${studentName(entry)}`
                    }
                    onClick={(event) => {
                      event.currentTarget.focus({ preventScroll: true });
                      setSelectedLesson(session);
                    }}
                  >
                    <span aria-hidden="true">›</span>
                  </button>
                )}
              </div>
            );
          })}
        </section>

        <section className="dashboard-panel" aria-labelledby="dashboard-attention-heading">
          <div className="dashboard-panel-head">
            <h2 id="dashboard-attention-heading">Needs attention</h2>
            {canSeeActionCentre && <Link to="/waiting">View Action Centre</Link>}
          </div>
          <div className="dashboard-attention-list">
            {canSeePayments && overdueCount > 0 && (
              <Link to="/reports/unpaid-lessons">
                <strong>{overdueCount}</strong> overdue lesson payments
              </Link>
            )}
            {canSeeRequests && requestCount > 0 && (
              <Link to="/waiting?queue=requests">
                <strong>{requestCount}</strong> lesson requests awaiting approval
              </Link>
            )}
            {canSeeOperationsReport && clashCount > 0 && (
              <Link to="/waiting?queue=clashes">
                <strong>{clashCount}</strong> student timetable clashes
              </Link>
            )}
            {canSeeOperationsReport && exceptionCount > 0 && (
              <Link to="/waiting?queue=exceptions">
                <strong>{exceptionCount}</strong> booking-system exceptions needing attention
              </Link>
            )}
            {canSeeWaitlist && waitlistCount > 0 && (
              <Link to="/waiting?queue=waitlist">
                <strong>{waitlistCount}</strong> waiting list approvals
              </Link>
            )}
            {unmatchedCount > 0 && (
              <Link to="/waiting?queue=unmatched">
                <strong>{unmatchedCount}</strong> unmatched items
              </Link>
            )}
            {unassignedPupilCount > 0 && (
              <Link to="/schools">
                <strong>{unassignedPupilCount}</strong> School{" "}
                {unassignedPupilCount === 1 ? "pupil has" : "pupils have"} no tutor
              </Link>
            )}
            {canSeeActionCentre && actionCentre.failed && (
              <div className="error">
                Some Action Centre sections could not be loaded, so this list may be incomplete.
              </div>
            )}
            {nothingNeedsAttention({
              loading: attentionLoading,
              failed: attentionFailed,
              operationsCount,
              unmatchedCount,
              overdueCount,
              unassignedPupilCount,
            }) && <p className="empty">Nothing needs attention right now.</p>}
          </div>
        </section>
      </div>

      {!today.loading && !today.error && <SchoolDayPanel entries={todayAppointments} now={now} />}

      <section
        className="dashboard-panel dashboard-notes"
        id="notes-to-write"
        aria-labelledby="dashboard-notes-heading"
      >
        <div className="dashboard-panel-head">
          <div>
            <h2 id="dashboard-notes-heading">Teaching follow-up</h2>
            <span className="small muted">
              {showAllFollowUp
                ? `Every lesson with no published note · last ${FOLLOW_UP_ALL_LOOKBACK_DAYS} days`
                : `Recent lessons with no published note · last ${NOTES_LOOKBACK_DAYS} days`}
            </span>
          </div>
        </div>
        {followUpLoading && <p className="empty">Loading teaching follow-up…</p>}
        {!followUpLoading && followUpError && (
          <div className="error">Teaching follow-up could not be loaded.</div>
        )}
        {!followUpLoading && !followUpError && noteTasks.length === 0 && (
          <p className="empty">You&rsquo;re up to date with your lesson notes.</p>
        )}
        {!followUpLoading &&
          !followUpError &&
          visibleNoteTasks.map((entry) => {
            const label = `${entry.studentName ?? entry.customerName ?? "Lesson"} · ${formatDate(
              entry.startsAt,
            )}`;
            return (
              <div className="dashboard-note-row" key={entry.bookingId}>
                <input
                  type="checkbox"
                  className="dashboard-note-select"
                  aria-label={`Select ${label} to remove from follow-up`}
                  checked={selectedFollowUp.has(entry.bookingId)}
                  disabled={followUpBusy}
                  onChange={(event) =>
                    toggleFollowUpSelection(entry.bookingId, event.currentTarget.checked)
                  }
                />
                <div className="dashboard-note-body">
                  <DashboardStudentName entry={entry} />
                  <div className="small muted">
                    {formatDate(entry.startsAt)} · {formatTime(entry.startsAt)} ·{" "}
                    {entry.serviceName ?? entry.instrument ?? "Lesson"} ·{" "}
                    {dashboardLessonNoteState(entry)}
                  </div>
                </div>
                <Link to={noteHrefFor(entry)}>
                  {entry.lessonId === null
                    ? entry.isSchools
                      ? "Write School note"
                      : "Write lesson note"
                    : "Finish draft"}
                </Link>
              </div>
            );
          })}
        {followUpProblem && <div className="error">{followUpProblem}</div>}
        {lastDismissed !== null && (
          <p className="dashboard-note-undo small" role="status">
            Removed {lastDismissed.length === 1 ? "1 lesson" : `${lastDismissed.length} lessons`}{" "}
            from your follow-up list. You can still write the note from the Diary.{" "}
            <button
              type="button"
              className="link"
              disabled={followUpBusy}
              onClick={() => void undoLastDismissal()}
            >
              Undo
            </button>
          </p>
        )}
        {!followUpLoading && !followUpError && (
          <div className="dashboard-note-actions">
            <div className="dashboard-note-actions-left">
              {visibleNoteTasks.length > 0 && (
                <button
                  type="button"
                  className="quiet"
                  disabled={followUpBusy || selectedVisible.length === 0}
                  onClick={() => void removeSelectedFromFollowUp()}
                >
                  {followUpBusy
                    ? "Working…"
                    : selectedVisible.length === 0
                      ? "Remove selected from follow-up"
                      : `Remove ${selectedVisible.length} from follow-up`}
                </button>
              )}
              {visibleNoteTasks.length > 0 && (
                <span className="small muted">
                  Tick a lesson you don&rsquo;t plan to write up, then remove it.
                </span>
              )}
            </div>
            {visibleNoteTasks.length < noteTasks.length ? (
              <button
                type="button"
                className="quiet"
                onClick={() => setFollowUpVisibleCount((current) => current + FOLLOW_UP_LIMIT)}
              >
                Show {Math.min(FOLLOW_UP_LIMIT, noteTasks.length - visibleNoteTasks.length)} more
              </button>
            ) : !showAllFollowUp ? (
              <button
                type="button"
                className="quiet"
                onClick={() => {
                  setShowAllFollowUp(true);
                  setFollowUpVisibleCount((current) => current + FOLLOW_UP_LIMIT);
                }}
              >
                Show older lessons
              </button>
            ) : (
              <button
                type="button"
                className="quiet"
                onClick={() => {
                  setShowAllFollowUp(false);
                  setFollowUpVisibleCount(FOLLOW_UP_LIMIT);
                }}
              >
                Show recent only
              </button>
            )}
          </div>
        )}
      </section>

      {selectedLesson && (
        <LessonActionDrawer
          key={selectedLesson.appointmentId}
          session={selectedLesson}
          // Today rows are the viewer's own server-scoped lessons, so this
          // deliberately mirrors the Diary's appointment-management authority.
          canManage
          onClose={() => setSelectedLesson(null)}
          onRefresh={refreshAppointmentSourcesInPlace}
          onChanged={refreshAppointmentSources}
        />
      )}
    </div>
  );
}
